import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  Package,
  Search,
  Loader2,
  AlertCircle,
  Clock,
  DollarSign,
  RefreshCw,
  UserPlus,
  Plus,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { API } from "@/App";
import { format } from "date-fns";
import { getDateLocale } from "@/utils/dateUtils";
import { formatTurkishCurrency } from "@/utils/numberUtils";
import { useLocalizedData } from "@/contexts/LocalizedDataContext";
import { useTranslation } from "react-i18next";

export default function AllPackagesModule({ businessId }) {
  const navigate = useNavigate();
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "admin", "packages"]);
  const { getLocalizedBranch } = useLocalizedData();

  const [packages, setPackages] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  // Trainer assignment modal
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assigningPkg, setAssigningPkg] = useState(null);
  const [selectedTrainerId, setSelectedTrainerId] = useState("none");
  const [isAssigning, setIsAssigning] = useState(false);

  // İstenen Bağımsız Filtreler:
  const [packageStatusFilter, setPackageStatusFilter] = useState("all"); // all, ongoing, completed, cancelled
  const [memberStatusFilter, setMemberStatusFilter] = useState("all");   // all, active, inactive
  const [debtFilter, setDebtFilter] = useState("all");                   // all, with_debt, no_debt

  // Sıralama (Sorting)
  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");

  useEffect(() => {
    if (businessId) {
      fetchPackages();
      fetchTrainers();
    }
  }, [businessId]);

  const fetchTrainers = async () => {
    try {
      const res = await fetch(`${API}/businesses/${businessId}/trainers`, { credentials: "include" });
      if (res.ok) setTrainers(await res.json());
    } catch (error) {
      console.error("Error fetching trainers:", error);
    }
  };

  const fetchPackages = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/businesses/${businessId}/all-member-packages`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setPackages(data.packages || []);
      } else {
        toast.error(t("packages:toast_paketlerYuklenemedi"));
      }
    } catch (error) {
      toast.error(t("common:errorOccurred"));
    } finally {
      setLoading(false);
    }
  };

  const calculatePackageDebt = (pkg) => {
    const isRefundCancel = pkg.status === "cancelled" && pkg.cancel_type === "refund";
    if (isRefundCancel) return 0;

    const price = Number(pkg.sale_price ?? pkg.price ?? 0);
    const paid = Number(pkg.paid_amount ?? 0);
    const mathDebt = Math.max(0, price - paid);

    const rawDebt = pkg.debt !== undefined && pkg.debt !== null ? Number(pkg.debt) : null;
    if (rawDebt !== null && !isNaN(rawDebt) && rawDebt > 0) {
      return rawDebt;
    }
    return mathDebt;
  };

  // Dinamik Özet İstatistikleri
  const dynamicSummary = useMemo(() => {
    let totalDebt = 0;
    let debtCount = 0;
    let totalSessions = 0;

    packages.forEach((pkg) => {
      const debt = calculatePackageDebt(pkg);
      if (debt > 0) {
        totalDebt += debt;
        debtCount += 1;
      }
      totalSessions += Number(pkg.remaining_sessions ?? 0);
    });

    return {
      total_packages: packages.length,
      total_debt: totalDebt,
      packages_with_debt: debtCount,
      total_remaining_sessions: totalSessions,
    };
  }, [packages]);

  // Sıralama İkonu Bileşeni
  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3.5 h-3.5 ml-1 opacity-40 inline" />;
    return sortDirection === "asc"
      ? <ArrowUp className="w-3.5 h-3.5 ml-1 inline text-navy" />
      : <ArrowDown className="w-3.5 h-3.5 ml-1 inline text-navy" />;
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Filtrelenmiş ve Sıralanmış Liste
  const filteredAndSortedPackages = useMemo(() => {
    return packages
      .filter((pkg) => {
        const isCancelled = Boolean(pkg.status === "cancelled");
        const isFrozen = Boolean(pkg.status === "frozen" || pkg.is_frozen === true);
        const isInactive = Boolean(
          !isCancelled && !isFrozen && 
          (pkg.status === "inactive" || pkg.is_active === false)
        );
        
        const rem = Number(pkg.remaining_sessions ?? 0);
        const isCompleted = !isCancelled && !isFrozen && !isInactive && rem === 0;
        const isOngoing = !isCancelled && !isFrozen && !isInactive && rem > 0;

        if (packageStatusFilter === "ongoing") {
          if (!isOngoing) return false;
        } else if (packageStatusFilter === "completed") {
          if (!isCompleted) return false;
        } else if (packageStatusFilter === "frozen") {
          if (!isFrozen) return false;
        } else if (packageStatusFilter === "inactive") {
          if (!isInactive) return false;
        } else if (packageStatusFilter === "cancelled") {
          if (!isCancelled) return false;
        }

        // 2. ÜYE DURUMU FİLTRESİ (Aktif / Pasif)
        const isMemberInactive = pkg.member_status === "inactive";
        if (memberStatusFilter === "active" && isMemberInactive) return false;
        if (memberStatusFilter === "inactive" && !isMemberInactive) return false;

        // 3. BORÇ DURUMU FİLTRESİ
        const debt = calculatePackageDebt(pkg);
        if (debtFilter === "with_debt" && debt <= 0) return false;
        if (debtFilter === "no_debt" && debt > 0) return false;

        // 4. ARAMA FİLTRESİ
        if (searchQuery) {
          const q = searchQuery.toLowerCase();
          const matchName = (pkg.member_name || "").toLowerCase().includes(q);
          const matchNo = (pkg.member_no || "").toLowerCase().includes(q);
          const matchPkg = (pkg.package_name || "").toLowerCase().includes(q);
          const matchPkgNo = (pkg.package_no || "").toLowerCase().includes(q);
          if (!matchName && !matchNo && !matchPkg && !matchPkgNo) return false;
        }

        return true;
      })
      .sort((a, b) => {
        let valA, valB;
        switch (sortField) {
          case "member_name":
            valA = (a.member_name || "").toLowerCase();
            valB = (b.member_name || "").toLowerCase();
            return sortDirection === "asc" ? valA.localeCompare(valB, "tr") : valB.localeCompare(valA, "tr");
          case "package_no":
            valA = (a.package_no || "").toLowerCase();
            valB = (b.package_no || "").toLowerCase();
            return sortDirection === "asc" ? valA.localeCompare(valB, "tr") : valB.localeCompare(valA, "tr");
          case "package_name":
            valA = (a.package_name || "").toLowerCase();
            valB = (b.package_name || "").toLowerCase();
            return sortDirection === "asc" ? valA.localeCompare(valB, "tr") : valB.localeCompare(valA, "tr");
          case "remaining_sessions":
            valA = Number(a.remaining_sessions ?? 0);
            valB = Number(b.remaining_sessions ?? 0);
            return sortDirection === "asc" ? valA - valB : valB - valA;
          case "sale_price":
            valA = Number(a.sale_price ?? a.price ?? 0);
            valB = Number(b.sale_price ?? b.price ?? 0);
            return sortDirection === "asc" ? valA - valB : valB - valA;
          case "debt":
            valA = calculatePackageDebt(a);
            valB = calculatePackageDebt(b);
            return sortDirection === "asc" ? valA - valB : valB - valA;
          case "created_at":
          default:
            valA = new Date(a.created_at || 0).getTime();
            valB = new Date(b.created_at || 0).getTime();
            return sortDirection === "asc" ? valA - valB : valB - valA;
        }
      });
  }, [packages, packageStatusFilter, memberStatusFilter, debtFilter, searchQuery, sortField, sortDirection]);

  // Paket Durum Rozeti (İptal / Tamamlandı / Devam Ediyor)
  const getPackageStatusBadge = (pkg) => {
    if (pkg.is_deleted === true || pkg.status === "deleted") {
      return <Badge variant="destructive" className="bg-stone-600 hover:bg-stone-600">Silindi</Badge>;
    }
    if (pkg.status === "cancelled") {
      return <Badge variant="destructive">İptal</Badge>;
    }
    if (pkg.status === "frozen" || pkg.is_frozen === true) {
      return <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100">Donduruldu</Badge>;
    }
    if (pkg.status === "inactive" || pkg.is_active === false) {
      return <Badge variant="secondary" className="bg-slate-100 text-slate-600">Pasif</Badge>;
    }
    if (Number(pkg.remaining_sessions ?? 0) === 0) {
      return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Tamamlandı</Badge>;
    }
    return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Devam Ediyor</Badge>;
  };

  const openAssignModal = (pkg) => {
    setAssigningPkg(pkg);
    setSelectedTrainerId(pkg.trainer_id || "none");
    setAssignModalOpen(true);
  };

  const handleAssignTrainer = async () => {
    if (!assigningPkg) return;
    setIsAssigning(true);
    try {
      const trainerId = selectedTrainerId === "none" ? null : selectedTrainerId;
      const res = await fetch(`${API}/member-packages/${assigningPkg.mp_id}/assign-trainer`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ trainer_id: trainerId }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(t("members:trainerAssigned"));
        setPackages((prev) =>
          prev.map((p) =>
            p.mp_id === assigningPkg.mp_id
              ? { ...p, trainer_id: trainerId, trainer_name: data.trainer_name || "" }
              : p
          )
        );
        setAssignModalOpen(false);
      } else {
        const err = await res.json();
        toast.error(err.detail || t("common:trainerAssignFailed"));
      }
    } catch (error) {
      toast.error(t("common:errorOccurred"));
    } finally {
      setIsAssigning(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-navy" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading font-bold text-xl text-navy">{t("members:allPackages")}</h2>
          <p className="text-grey text-sm">
            {filteredAndSortedPackages.length} paket listeleniyor (Toplam: {packages.length})
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            onClick={() => navigate("/dashboard/members?action=sell-package")}
            size="sm"
            className="rounded-xl bg-navy hover:bg-navy/90 text-white"
            data-testid="all-packages-sell-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Yeni Paket Sat
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchPackages}
            className="rounded-xl"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            {t("common:refresh")}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-navy/10 flex items-center justify-center">
                <Package className="w-5 h-5 text-navy" />
              </div>
              <div>
                <p className="text-xs text-grey">{t("packages:totalPackage")}</p>
                <p className="font-bold text-lg text-navy">{dynamicSummary.total_packages}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-xs text-grey">{t("payments:totalDebt")}</p>
                <p className="font-bold text-lg text-red-600">
                  {formatTurkishCurrency(dynamicSummary.total_debt)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-grey">{t("packages:borcluPaket")}</p>
                <p className="font-bold text-lg text-amber-600">{dynamicSummary.packages_with_debt}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-grey">{t("common:remainingLessons")}</p>
                <p className="font-bold text-lg text-green-600">{dynamicSummary.total_remaining_sessions}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters (Anlık Filtreleme - Yenilenme Yok) */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-grey" />
                <Input
                  placeholder={t("common:searchMember")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 rounded-xl"
                  data-testid="package-search-input"
                />
              </div>
            </div>

            <div className="flex gap-2 flex-wrap items-center">
              {/* Paket Durumu Filtresi */}
              <Select value={packageStatusFilter} onValueChange={setPackageStatusFilter}>
                <SelectTrigger className="w-[170px] rounded-xl bg-white" data-testid="package-status-filter">
                  <SelectValue placeholder="Paket Durumu" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Paket Durumları</SelectItem>
                  <SelectItem value="ongoing">Devam Edenler</SelectItem>
                  <SelectItem value="completed">Tamamlananlar</SelectItem>
                  <SelectItem value="frozen">Dondurulan Paketler</SelectItem>
                  <SelectItem value="inactive">Pasif Paketler</SelectItem>
                  <SelectItem value="cancelled">İptal Edilenler</SelectItem>
                </SelectContent>
              </Select>

              {/* 2. Üye Durumu Filtresi */}
              <Select value={memberStatusFilter} onValueChange={setMemberStatusFilter}>
                <SelectTrigger className="w-[140px] rounded-xl bg-white">
                  <SelectValue placeholder="Üye Durumu" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Üyeler</SelectItem>
                  <SelectItem value="active">Aktif Üyeler</SelectItem>
                  <SelectItem value="inactive">Pasif Üyeler</SelectItem>
                </SelectContent>
              </Select>

              {/* 3. Borç Durumu Filtresi */}
              <Select value={debtFilter} onValueChange={setDebtFilter}>
                <SelectTrigger className="w-[130px] rounded-xl bg-white">
                  <SelectValue placeholder={t("members:debt")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("common:allDebts")}</SelectItem>
                  <SelectItem value="with_debt">{t("common:withDebt")}</SelectItem>
                  <SelectItem value="no_debt">{t("common:noDebt")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Packages Table */}
      <Card className="border-0 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b bg-slate-50/80 flex items-center justify-between">
          <p className="text-sm font-semibold text-navy">
            Listelenen Paketler: <span className="font-bold text-navy">{filteredAndSortedPackages.length}</span>
            {filteredAndSortedPackages.length !== packages.length && (
              <span className="text-xs text-grey font-normal ml-1">
                (toplam {packages.length} kayıt içerisinden filtrelendi)
              </span>
            )}
          </p>
        </div>

        <CardContent className="p-0">
          {filteredAndSortedPackages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-grey">
              <Package className="w-12 h-12 mb-4 opacity-50" />
              <p>{t("packages:filtrelereUygunPaketBulunamadi")}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead
                      className="cursor-pointer hover:bg-slate-100 select-none"
                      onClick={() => handleSort("member_name")}
                    >
                      {t("common:member")} <SortIcon field="member_name" />
                    </TableHead>
                    <TableHead className="text-center">Üye Durumu</TableHead>
                    <TableHead
                      className="cursor-pointer hover:bg-slate-100 select-none"
                      onClick={() => handleSort("package_no")}
                    >
                      Paket No <SortIcon field="package_no" />
                    </TableHead>
                    <TableHead
                      className="cursor-pointer hover:bg-slate-100 select-none"
                      onClick={() => handleSort("package_name")}
                    >
                      {t("packages:package")} <SortIcon field="package_name" />
                    </TableHead>
                    <TableHead>{t("common:trainer")}</TableHead>
                    <TableHead
                      className="text-center cursor-pointer hover:bg-slate-100 select-none"
                      onClick={() => handleSort("remaining_sessions")}
                    >
                      {t("packages:remainingTotal")} <SortIcon field="remaining_sessions" />
                    </TableHead>
                    <TableHead
                      className="text-right cursor-pointer hover:bg-slate-100 select-none"
                      onClick={() => handleSort("sale_price")}
                    >
                      {t("payments:salePrice")} <SortIcon field="sale_price" />
                    </TableHead>
                    <TableHead className="text-right">{t("common:paidAmount")}</TableHead>
                    <TableHead
                      className="text-right cursor-pointer hover:bg-slate-100 select-none"
                      onClick={() => handleSort("debt")}
                    >
                      {t("packages:remainingDebt")} <SortIcon field="debt" />
                    </TableHead>
                    <TableHead className="text-center">{t("common:paymentStatus")}</TableHead>
                    <TableHead className="text-center">Paket Durumu</TableHead>
                    <TableHead
                      className="cursor-pointer hover:bg-slate-100 select-none"
                      onClick={() => handleSort("created_at")}
                    >
                      {t("common:createdAt")} <SortIcon field="created_at" />
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAndSortedPackages.map((pkg) => {
                    const currentDebt = calculatePackageDebt(pkg);
                    return (
                      <TableRow
                        key={pkg.mp_id}
                        className={pkg.member_status === "inactive" ? "opacity-60" : ""}
                        data-testid={`package-row-${pkg.mp_id}`}
                      >
                        <TableCell>
                          <div>
                            <p className="font-medium text-navy">{pkg.member_name}</p>
                            <p className="text-xs text-grey">{pkg.member_no}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          {pkg.member_status === "inactive" ? (
                            <Badge variant="secondary" className="bg-slate-100 text-slate-500">Pasif</Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Aktif</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="font-mono text-xs text-navy" data-testid={`package-no-${pkg.mp_id}`}>
                            {pkg.package_no || "-"}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{pkg.package_name || "Bilinmiyor"}</p>
                            {pkg.branches && pkg.branches.length > 0 && (
                              <div className="flex gap-1 mt-1">
                                {pkg.branches.map((b) => (
                                  <Badge key={b} variant="outline" className="text-xs">
                                    {b}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <button
                            onClick={() => openAssignModal(pkg)}
                            className="text-left hover:bg-slate-50 rounded-lg p-1 -m-1 transition-colors w-full"
                            data-testid={`assign-trainer-${pkg.mp_id}`}
                          >
                            {pkg.trainer_name ? (
                              <span className="text-sm text-navy font-medium">{pkg.trainer_name}</span>
                            ) : (
                              <span className="text-xs text-amber-600 flex items-center gap-1">
                                <UserPlus className="w-3.5 h-3.5" />
                                Antrenör Ata
                              </span>
                            )}
                          </button>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <span
                              className={
                                (pkg.remaining_sessions || 0) > 0
                                  ? "font-bold text-green-600"
                                  : "text-grey"
                              }
                            >
                              {pkg.remaining_sessions || 0}
                            </span>
                            <span className="text-grey">/</span>
                            <span className="text-grey">{pkg.total_sessions || 0}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="text-navy">
                            {formatTurkishCurrency(pkg.sale_price || pkg.price || 0)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="text-green-600">
                            {formatTurkishCurrency(pkg.paid_amount || 0)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          {currentDebt > 0 ? (
                            <span className="font-bold text-red-600">
                              {formatTurkishCurrency(currentDebt)}
                            </span>
                          ) : (
                            <span className="text-green-600">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {currentDebt <= 0 ? (
                            <Badge className="bg-green-100 text-green-700">{t("common:completed")}</Badge>
                          ) : (pkg.paid_amount || 0) > 0 ? (
                            <Badge className="bg-amber-100 text-amber-700">{t("payments:partialPayment")}</Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-700">Ödenmedi</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {getPackageStatusBadge(pkg)}
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-grey">
                            {pkg.created_at
                              ? format(new Date(pkg.created_at), "dd MMM yyyy", { locale: getDateLocale() })
                              : "-"}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Trainer Assignment Modal */}
      <Dialog open={assignModalOpen} onOpenChange={setAssignModalOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t("common:changeAssignTrainer")}</DialogTitle>
          </DialogHeader>
          {assigningPkg && (
            <div className="space-y-4 mt-2">
              <div className="p-3 bg-slate-50 rounded-xl text-sm">
                <p className="font-medium text-navy">{assigningPkg.member_name}</p>
                <p className="text-grey">{assigningPkg.package_name}</p>
              </div>
              <div>
                <Label>{t("common:trainer")}</Label>
                <Select value={selectedTrainerId} onValueChange={setSelectedTrainerId}>
                  <SelectTrigger className="rounded-xl" data-testid="assign-trainer-select">
                    <SelectValue placeholder={t("common:selectTrainer")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t("trainers:noTrainer")}</SelectItem>
                    {trainers
                      .filter((t) => t.is_active !== false)
                      .filter((t) => {
                        const pkgBranches = assigningPkg.branches || [];
                        if (pkgBranches.length === 0) return true;
                        const trainerBranches = t.branches || [];
                        return pkgBranches.some((b) => trainerBranches.includes(b));
                      })
                      .map((t) => (
                        <SelectItem key={t.trainer_id} value={t.trainer_id}>
                          {t.name} {t.surname}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {assigningPkg.branches?.length > 0 && (
                  <p className="text-xs text-blue-600 mt-1">
                    Sadece {assigningPkg.branches.map((b) => getLocalizedBranch(b)).join(", ")} branşındaki antrenörler gösteriliyor
                  </p>
                )}
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setAssignModalOpen(false)} className="rounded-xl">
              İptal
            </Button>
            <Button
              onClick={handleAssignTrainer}
              disabled={isAssigning}
              className="bg-navy hover:bg-navy/90 rounded-xl"
              data-testid="confirm-assign-trainer-btn"
            >
              {isAssigning ? "Kaydediliyor..." : t("common:save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}